import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Avatar, Box, Button, Chip, IconButton, Skeleton, Switch, Typography } from '@mui/material';
import { MdArrowBack, MdEdit, MdInventory2 } from 'react-icons/md';
import { getBogoById, updateBogo } from '../../Webservice/offerAPI';
import { ImageGallery } from './OfferImages';
import { FONT, StateChip, fmtDate, imageUrl, itemInfo, period, stateOf, summary, yellowBtn } from './bogoShared';

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const Card = ({ title, accent, children, right }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5 }}>{title}</Typography>
      {right}
    </Box>
    {children}
  </Box>
);
const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.85, borderBottom: '1px dashed #e2e8f0', '&:last-of-type': { borderBottom: 'none' } }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);
const Tile = ({ label, value, sub, color }) => (
  <Box sx={{ p: 1.5, borderRadius: '12px', background: `${color}12`, border: `1px solid ${color}33`, minWidth: 0 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 19, fontWeight: 800, color: '#0f172a', lineHeight: 1.25, wordBreak: 'break-word' }}>{value}</Typography>
    {sub && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', mt: 0.25 }}>{sub}</Typography>}
  </Box>
);

function ViewBogoOffer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bogo, setBogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await getBogoById(id);
      const b = res?.data || res?.bogo;
      if (!b) throw new Error('missing');
      setBogo(b);
    } catch {
      setError('This BOGO offer could not be found.');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    try {
      await updateBogo(id, { isActive: !bogo.isActive });
      load();
    } catch { /* the service shows the error */ }
  };

  if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={120} sx={{ borderRadius: '14px', mb: 2 }} /><Skeleton variant="rounded" height={320} sx={{ borderRadius: '14px' }} /></Box>;
  if (error || !bogo) {
    return <Box sx={{ p: 3, maxWidth: 520, mx: 'auto' }}><Alert severity="error" sx={{ mb: 2 }}>{error}</Alert><Button onClick={() => navigate('/offers/bogo')} sx={yellowBtn}>Back to offers</Button></Box>;
  }

  const items = (bogo.buyProducts || []).map(itemInfo).filter(Boolean);
  const kind = items[0]?.kind === 'Category' ? 'Categories' : 'Products';
  const by = bogo.createdBy && typeof bogo.createdBy === 'object' ? `${bogo.createdBy.firstName || ''} ${bogo.createdBy.lastName || ''}`.trim() || bogo.createdBy.email : null;
  const state = stateOf(bogo);
  const discount = Number(bogo.discountPercentage ?? 100);
  const pictures = bogo.product_images || [];
  const cover = pictures[0] ? imageUrl(pictures[0]) : items.find((i) => i.image)?.image;

  // products in a BOGO offer are sold at MRP, so one deal is worked out on the first product's MRP
  const priced = items.find((i) => i.mrp > 0);
  const buyQty = Number(bogo.buyQuantity) || 1;
  const getQty = Number(bogo.getQuantity) || 1;
  const split = priced ? (() => {
    const buyTotal = priced.mrp * buyQty;
    const rewardFull = priced.mrp * getQty;
    const rewardPay = rewardFull * (1 - discount / 100);
    const youPay = buyTotal + rewardPay;
    return { buyTotal, rewardFull, rewardOff: rewardFull - rewardPay, rewardPay, youPay, perUnit: youPay / (buyQty + getQty), regular: priced.price };
  })() : null;
  const saves = split ? +split.rewardOff.toFixed(2) : 0;

  const liveButOut = state === 'live' && bogo.inStock === false;
  const stockValue = bogo.inStock === false ? 'Out of stock' : bogo.stockLeft === null || bogo.stockLeft === undefined ? 'Always available' : `${bogo.stockLeft} deal${bogo.stockLeft === 1 ? '' : 's'}`;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <IconButton onClick={() => navigate('/offers/bogo')} sx={{ background: '#f1f5f9' }}><MdArrowBack size={18} /></IconButton>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a', lineHeight: 1.2 }}>{bogo.title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <StateChip state={state} />
            {bogo.inStock === false && <Chip size="small" label="Out of stock" sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: '#fee2e2', color: '#991b1b' }} />}
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{period(bogo)}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>{bogo.isActive ? 'On' : 'Off'}</Typography>
          <Switch checked={!!bogo.isActive} onChange={toggle} />
        </Box>
        <Button startIcon={<MdEdit />} variant="contained" onClick={() => navigate(`/offers/bogo/edit/${id}`)} sx={yellowBtn}>Edit offer</Button>
      </Box>

      {liveButOut && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <b>This deal is live but out of stock.</b> The chosen products do not have enough units for {bogo.buyQuantity} bought plus {bogo.getQuantity} reward, so customers cannot get it. Add stock or switch the offer off.
        </Alert>
      )}

      {/* Hero: cover picture + the deal */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '170px minmax(0, 1fr)' }, p: 2, borderRadius: '16px', mb: 2, color: '#fff', background: 'linear-gradient(135deg,#6366f1,#4338ca)', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: { xs: 120, sm: 170 }, height: { xs: 120, sm: 170 }, borderRadius: '14px', overflow: 'hidden', background: 'rgba(255,255,255,.15)', display: 'grid', placeItems: 'center' }}>
          {cover ? <Box component="img" src={cover} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} /> : <MdInventory2 size={44} />}
          {pictures.length > 1 && <Box sx={{ position: 'absolute', right: 6, bottom: 6, px: 0.75, borderRadius: '6px', background: 'rgba(0,0,0,.6)', fontSize: 11, fontWeight: 700, fontFamily: FONT }}>{pictures.length} photos</Box>}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, opacity: 0.85, fontWeight: 700, letterSpacing: '1px' }}>THE DEAL</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: { xs: 24, md: 30 }, fontWeight: 800, lineHeight: 1.15 }}>{summary(bogo)}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, opacity: 0.92, mt: 0.5 }}>{bogo.description || 'No description.'}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
            {[`Buy ${bogo.buyQuantity}`, `Get ${bogo.getQuantity}`, discount >= 100 ? 'Free' : `${discount}% off`, kind].map((t) => (
              <Chip key={t} size="small" label={t} sx={{ fontFamily: FONT, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.3)' }} />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Key numbers */}
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, mb: 2 }}>
        <Tile label="Deals available" value={stockValue} color={bogo.inStock === false ? '#ef4444' : '#22c55e'} sub={bogo.inStock === false ? 'Not enough stock' : undefined} />
        <Tile label="Customer saves" value={saves ? inr(saves) : '—'} color="#16a34a" sub={priced ? `per deal on ${priced.name} (at MRP)` : 'Needs a product with an MRP'} />
        <Tile label={kind} value={items.length} color="#0ea5e9" sub="in this offer" />
        <Tile label="Runs" value={bogo.startDate || bogo.endDate ? (bogo.endDate ? `Until ${fmtDate(bogo.endDate)}` : `From ${fmtDate(bogo.startDate)}`) : 'Always on'} color="#f59e0b" />
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1.4fr)' }, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <Card title="Price split (one deal)" accent="#22c55e">
            {split ? (
              <>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', mb: 0.5 }}>On {priced.name}. Products in a BOGO offer are sold at MRP; the regular selling price does not apply.</Typography>
                <Row label={`Buy ${buyQty} × ${inr(priced.mrp)} (MRP)`} value={inr(split.buyTotal)} />
                <Row label={`Get ${getQty} × ${inr(priced.mrp)} (MRP)`} value={<><span style={{ textDecoration: 'line-through', color: '#94a3b8', marginRight: 6 }}>{inr(split.rewardFull)}</span>{discount >= 100 ? 'Free' : inr(split.rewardPay)}</>} />
                <Row label="Customer pays" value={<b>{inr(split.youPay)}</b>} />
                <Row label={`Per unit (${buyQty + getQty} units)`} value={inr(split.perUnit)} />
                {split.regular > 0 && split.regular < priced.mrp && <Row label="Regular selling price (not used)" value={<span style={{ color: '#94a3b8' }}>{inr(split.regular)}</span>} />}
              </>
            ) : <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', py: 1 }}>Add a product with an MRP to see the price split.</Typography>}
          </Card>
          <Card title="Pictures" accent="#ec4899">
            <ImageGallery images={pictures} />
          </Card>
          <Card title="Availability" accent="#0ea5e9">
            <Row label="Starts" value={bogo.startDate ? fmtDate(bogo.startDate) : 'Immediately'} />
            <Row label="Ends" value={bogo.endDate ? fmtDate(bogo.endDate) : 'No end date'} />
            <Row label="Visible to" value={(bogo.visibility || []).map((v) => <Chip key={v} size="small" label={v.toUpperCase()} sx={{ ml: 0.5, height: 20, fontWeight: 700 }} />)} />
            <Row label="Created" value={`${fmtDate(bogo.createdAt)}${by ? ` · ${by}` : ''}`} />
            <Row label="Last updated" value={fmtDate(bogo.updatedAt)} />
          </Card>
        </Box>

        <Card title={`${kind} in this offer`} accent="#22c55e" right={<Chip size="small" label={items.length} sx={{ fontWeight: 800, background: 'rgb(255 209 2)' }} />}>
          {items.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', py: 3, textAlign: 'center' }}>No items in this offer.</Typography>}
          {items.map((it, i) => (
            <Box key={it.id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: i < items.length - 1 ? '1px dashed #e2e8f0' : 'none', flexWrap: 'wrap' }}>
              <Avatar variant="rounded" src={it.image} sx={{ width: 46, height: 46, bgcolor: '#e0e7ff', color: '#3730a3' }}><MdInventory2 /></Avatar>
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5, lineHeight: 1.3 }}>{it.name}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{it.kind}{it.code ? ` · ${it.code}` : ''}</Typography>
              </Box>
              {it.stock !== undefined && <Chip size="small" label={it.stock > 0 ? `${it.stock} in stock` : 'Out of stock'} sx={{ fontWeight: 700, height: 22, background: it.stock > 0 ? '#dcfce7' : '#fee2e2', color: it.stock > 0 ? '#166534' : '#991b1b' }} />}
              {(it.mrp !== undefined || it.price !== undefined) && (
                <Box sx={{ minWidth: 92, textAlign: 'right' }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, lineHeight: 1.2 }}>{inr(it.mrp ?? it.price)}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: '#16a34a', fontWeight: 700 }}>OFFER PRICE (MRP)</Typography>
                  {it.price > 0 && it.mrp > it.price && <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#94a3b8', textDecoration: 'line-through' }}>{inr(it.price)} selling</Typography>}
                </Box>
              )}
            </Box>
          ))}
        </Card>
      </Box>
    </Box>
  );
}

export default ViewBogoOffer;
